import { useEffect, useMemo, useState } from 'react';
import { Space, Button, Text } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { Form, Spin, Divider } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getInventory } from 'queries';

import { JsonDataItem, type JsonData, type WithOptionalDataTest } from 'types';
import { PartsItem } from './parts-item.component';
import { parseErrors, Toast } from 'helpers';
import styles from './parts-list.module.scss';

type Props = {
  isClosed: boolean;
  isLocked: boolean;
  serviceId?: string;
  partsSelection?: JsonData;
  totalPrice: string;
  onSelect: (products: JsonData | undefined) => void;
} & WithOptionalDataTest;

const emptySelection = {
  id: null,
  qty: 1,
  price: '0.00',
  details: undefined,
  description: undefined,
  type: undefined,
};

export const PartsList: React.FC<Props> = observer((props) => {
  const { isClosed, onSelect, partsSelection, serviceId, totalPrice, isLocked } = props;
  const [selectedParts, setSelectedParts] = useState<JsonData | undefined>(partsSelection);

  const { isInitialLoading: isG2UPartsLoading, data: g2uParts } = useQuery(
    ['get-g2u-parts', 'technician', serviceId],
    getInventory,
    {
      enabled: !!serviceId && !isClosed,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const { isInitialLoading: isOWPartsLoading, data: owParts } = useQuery(['get-ow-parts', 'office'], getInventory, {
    enabled: !!serviceId && !isClosed,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const partsList = useMemo(() => [...(g2uParts ?? []), ...(owParts ?? [])], [g2uParts, owParts]);

  const handleQuantityChange = (id: number, qty: number) => {
    const updatedProducts = selectedParts ? selectedParts.map((p) => ({ ...p })) : [...[emptySelection]];
    const changeIdx = updatedProducts.findIndex((item) => item.id?.toString() === id.toString());
    updatedProducts[changeIdx].qty = qty;
    setSelectedParts(updatedProducts);
  };

  const handlePriceChange = (id: number, price: string) => {
    const updatedProducts = selectedParts ? selectedParts.map((p) => ({ ...p })) : [...[emptySelection]];
    const changeIdx = updatedProducts.findIndex((item) => item.id?.toString() === id.toString());
    updatedProducts[changeIdx].price = price;
    if (!updatedProducts[changeIdx].details) {
      updatedProducts[changeIdx].details = {};
    }
    updatedProducts[changeIdx].details.total_retail = price;
    setSelectedParts(updatedProducts);
  };

  const handleProductChange = (id: number | string, details: JsonDataItem | null) => {
    const updatedProducts = selectedParts ? selectedParts.map((p) => ({ ...p })) : [...[emptySelection]];

    const idx = updatedProducts.findIndex((item) => item.id?.toString() === id?.toString());
    const changeIdx = idx > 0 ? idx : updatedProducts.length - 1;

    updatedProducts[changeIdx].id = id;
    updatedProducts[changeIdx].price = details ? Number(details.total_retail).toFixed(2) : '0.00';
    updatedProducts[changeIdx].details = details;
    updatedProducts[changeIdx].description = details ? details.description : undefined;
    updatedProducts[changeIdx].type = details ? details.type : undefined;

    setSelectedParts(updatedProducts);
  };

  const handleAddPart = (part: JsonDataItem) => {
    setSelectedParts(
      selectedParts ? [...selectedParts.filter((item) => item.id !== part.id).map((p) => ({ ...p })), part] : [part],
    );
  };

  const handleRemovePart = (id: number) => {
    const updatedParts = selectedParts?.filter((item) => item.id !== id).map((p) => ({ ...p }));

    setSelectedParts(updatedParts?.length ? updatedParts : undefined);
  };

  useEffect(() => {
    onSelect(
      selectedParts
        ?.filter((p) => p !== emptySelection)
        .map((part) => {
          return { ...part };
        }),
    );
  }, [selectedParts]);

  return (
    <Space.$ direction={Space.Direction.Vertical} className={styles.partsList}>
      <Text.$ strong size={Text.Size.LG} className={styles.partsList__title}>
        Parts
      </Text.$>
      {isG2UPartsLoading || isOWPartsLoading ? (
        <Space.$ align={Space.Align.Center} direction={Space.Direction.Vertical}>
          <Spin />
        </Space.$>
      ) : (
        <>
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
            <Form.Item name="parts">
              <div>
                {selectedParts?.map((selectedPart) => {
                  return (
                    <PartsItem
                      key={selectedPart.id}
                      partsList={partsList.filter(
                        (listedPart) =>
                          !selectedParts.some((part) => part.id === listedPart?.ID) ||
                          listedPart?.ID === selectedPart.id,
                      )}
                      selectedPart={selectedPart}
                      isAttachedToJob={!!partsSelection && partsSelection.some((item) => item.id === selectedPart.id)}
                      handlePriceChange={handlePriceChange}
                      handleQtyChange={handleQuantityChange}
                      handleProductChange={handleProductChange}
                      handleRemoveProduct={handleRemovePart}
                    />
                  );
                })}

                <Button.$
                  type={Button.Type.Dashed}
                  block
                  dataTestId="add-another-part"
                  className={styles.partsList__action}
                  onClick={() => handleAddPart(emptySelection)}
                  style={{ marginTop: 8 }}
                  disabled={selectedParts?.some((part) => part.id === null) || isClosed || isLocked}
                >
                  + Add new
                </Button.$>
              </div>
            </Form.Item>
          </Space.$>

          {selectedParts && selectedParts.filter((part) => part.id).length > 0 && (
            <>
              <Divider />
              <div className={styles.partsList__total}>
                <Text.$ size={Text.Size.SM}>
                  Total <br />
                  <b>{selectedParts.filter((part) => part.id).length}</b> item{selectedParts.length > 1 ? 's' : ''}
                </Text.$>
                <Text.$>
                  <b>${totalPrice} </b> <small>inc GST</small>
                </Text.$>
              </div>
            </>
          )}
        </>
      )}
    </Space.$>
  );
});
